import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { AuthUser, Credentials, Licence, OtpChallenge } from '../domain/login.model';
import { validateCredentials } from '../domain/login.validation';
import { retryAfterOf, toLoginError } from '../domain/login-error';
import type { LoginGateway } from '../infrastructure/login.gateway';
import { credentialsSubmitted, otpResendRequested, otpSubmitted } from './login.actions';
import { selectCredentials, selectOtp, selectOtpToken } from './login.selectors';
import { loginActions } from './login.slice';

/**
 * WORKER, step 1: validate credentials, then authenticate. The server answers
 * with either an established session (trusted device → `'user' in res`) or an
 * OTP challenge. All I/O goes through the injected gateway (read from the saga
 * `context`; never the api client), so tests drive every branch with an
 * in-memory gateway. A rate-limit `retryAfter` is surfaced for the UI cooldown.
 */
function* credentialsWorker() {
  const credentials = (yield select(selectCredentials)) as Credentials;

  const errors = validateCredentials(credentials);
  if (Object.keys(errors).length > 0) {
    yield put(
      loginActions.credentialsFailed({
        error: { kind: 'validation', fields: errors },
        retryAfter: 0,
      }),
    );
    return;
  }

  yield put(loginActions.credentialsStarted());
  const gateway: LoginGateway = yield getContext('loginGateway');
  try {
    const res = (yield call(gateway.authenticate, credentials)) as
      | { user: AuthUser }
      | OtpChallenge;
    if ('user' in res) {
      const licence = (yield call(gateway.fetchCurrentLicence)) as Licence;
      yield put(loginActions.sessionEstablished({ user: res.user, licence }));
    } else {
      yield put(loginActions.otpRequired(res));
    }
  } catch (e) {
    yield put(
      loginActions.credentialsFailed({ error: toLoginError(e), retryAfter: retryAfterOf(e) }),
    );
  }
}

/**
 * WORKER, step 2: verify the OTP code with the session token, then fetch the
 * licence and establish the session. `rememberMe` is a page-owned preference
 * carried in the action payload (not stored in Redux).
 */
function* otpWorker(action: ReturnType<typeof otpSubmitted>) {
  const code = ((yield select(selectOtp)) as string[]).join('');
  const otpToken = (yield select(selectOtpToken)) as string | null;
  if (!otpToken || code.length !== 6) return;

  yield put(loginActions.otpStarted());
  const gateway: LoginGateway = yield getContext('loginGateway');
  try {
    const { user } = (yield call(gateway.verifyOtp, {
      otp_token: otpToken,
      code,
      remember_me: action.payload.rememberMe,
    })) as { user: AuthUser };
    const licence = (yield call(gateway.fetchCurrentLicence)) as Licence;
    yield put(loginActions.sessionEstablished({ user, licence }));
  } catch (e) {
    yield put(loginActions.otpFailed(toLoginError(e)));
  }
}

/** WORKER: re-send the OTP code for the current session token. */
function* resendWorker() {
  const otpToken = (yield select(selectOtpToken)) as string | null;
  if (!otpToken) return;

  yield put(loginActions.resendStarted());
  const gateway: LoginGateway = yield getContext('loginGateway');
  try {
    yield call(gateway.resendOtp, otpToken);
    yield put(loginActions.resendSucceeded());
  } catch (e) {
    yield put(loginActions.resendFailed(toLoginError(e)));
  }
}

/** WATCHERS. `takeLatest` so a fresh submit supersedes any in-flight request. */
export function* loginSaga() {
  yield all([
    takeLatest(credentialsSubmitted.type, credentialsWorker),
    takeLatest(otpSubmitted.type, otpWorker),
    takeLatest(otpResendRequested.type, resendWorker),
  ]);
}
